import { useEffect, useState } from "react";
import { type LucideIcon, MapPin, DollarSign, Shield, Bot } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { agentRoleLabel } from "@/lib/agent-display";
import { agentsApi, type CreateAgentInput, type Agent } from "@/api/agents";
import type { ModelCatalogueItem } from "@/api/models";
import {
  TEMPLATES,
  ADAPTERS,
  VM_SIZES,
  REGIONS,
  findAdapterCard,
  type Template,
} from "./data";

// ───────────────── shared bits ─────────────────

export function SectionHeader({
  eyebrow,
  title,
  desc,
}: {
  eyebrow?: string;
  title: string;
  desc?: string;
}) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted">
          {eyebrow}
        </p>
      )}
      <h1 className="m-0 text-[26px] font-semibold leading-tight tracking-[-0.015em] text-text-primary">
        {title}
      </h1>
      {desc && (
        <p className="mt-2 max-w-[560px] text-sm leading-relaxed text-text-secondary">
          {desc}
        </p>
      )}
    </div>
  );
}

export function FieldLabel({
  children,
  hint,
}: {
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-1.5 flex items-baseline justify-between">
      <label className="text-xs font-medium text-text-secondary">{children}</label>
      {hint && <span className="text-[11px] text-text-muted">{hint}</span>}
    </div>
  );
}

export function Pill({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "accent" | "warn";
}) {
  const toneClass =
    tone === "accent"
      ? "bg-accent/10 text-accent"
      : tone === "warn"
        ? "bg-[#f59e0b]/10 text-[#b45309]"
        : "bg-surface-hover text-text-secondary";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${toneClass}`}
    >
      {children}
    </span>
  );
}

export function SelectableCard({
  selected,
  onClick,
  children,
  className = "",
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full rounded-xl border p-4 text-left transition-all ${
        selected
          ? "border-accent bg-accent/[0.04] shadow-[0_0_0_3px_rgba(16,163,127,0.10)]"
          : "border-border bg-surface hover:bg-surface-hover"
      } ${className}`}
    >
      {children}
    </button>
  );
}

// Small circular avatar placeholder for the reports-to pills.
function MiniAvatar({ size = 20 }: { size?: number }) {
  return (
    <div
      className="shrink-0 rounded-md bg-gradient-to-br from-accent to-accent-hover text-white flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <Bot size={Math.round(size * 0.55)} />
    </div>
  );
}

type Updater = (patch: Partial<CreateAgentInput> & { template?: string }) => void;

// ═════════════════ STEP 1 — TEMPLATE ═════════════════
export function StepTemplate({
  templateId,
  set,
  onSelectTemplate,
}: {
  templateId: string;
  set: Updater;
  onSelectTemplate: (t: Template) => void;
}) {
  return (
    <div>
      <SectionHeader
        eyebrow="Step 1 of 5 · Template"
        title="What should this agent do?"
        desc="Start from a template to get sensible defaults for role, runtime and capabilities. You can change anything later."
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {TEMPLATES.map((t) => {
          const Icon: LucideIcon = t.icon;
          const selected = templateId === t.id;
          return (
            <SelectableCard
              key={t.id}
              selected={selected}
              onClick={() => {
                onSelectTemplate(t);
                set({ /* triggered by parent handler via onSelectTemplate */ });
              }}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] ${
                    selected
                      ? "bg-accent/12 text-accent"
                      : "bg-surface-hover text-text-secondary"
                  }`}
                  style={{ backgroundColor: selected ? "rgba(16,163,127,0.12)" : undefined }}
                >
                  <Icon size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-0.5 flex items-center gap-2">
                    <h3 className="m-0 text-sm font-semibold text-text-primary">{t.name}</h3>
                    {t.recommended && <Pill tone="accent">Recommended</Pill>}
                  </div>
                  <p className="m-0 text-[12.5px] leading-snug text-text-secondary">{t.desc}</p>
                  {t.capabilities && (
                    <p className="mt-2.5 m-0 font-mono text-[11px] text-text-muted">
                      {t.capabilities}
                    </p>
                  )}
                </div>
              </div>
            </SelectableCard>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════════ STEP 2 — IDENTITY ═════════════════
export function StepIdentity({
  form,
  set,
  agents,
  departments,
}: {
  form: CreateAgentInput;
  set: Updater;
  agents: Agent[];
  departments: string[];
}) {
  const deptOptions = departments.length
    ? departments
    : ["IT", "Finance", "People", "Strategy", "Operations", "Engineering", "Sales", "Support"];

  return (
    <div>
      <SectionHeader
        eyebrow="Step 2 of 5 · Identity"
        title="Who is this agent, in your org?"
        desc="Give it a name, a role, a manager. This is what appears in the org chart and in chat handles."
      />

      <div className="grid max-w-[680px] grid-cols-1 gap-4 md:grid-cols-2">
        <div className="md:col-span-2">
          <FieldLabel hint="How people will @mention them">Agent name</FieldLabel>
          <Input
            placeholder="e.g. Aria Chen"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>

        <div>
          <FieldLabel hint="Shown under the agent's name across the app">Role / Job Title</FieldLabel>
          <Input
            placeholder="e.g. Head of Talent Acquisition"
            value={form.title ?? ""}
            onChange={(e) => set({ title: e.target.value })}
          />
        </div>

        <div>
          <FieldLabel>Department</FieldLabel>
          <select
            value={form.department ?? ""}
            onChange={(e) => set({ department: e.target.value || null })}
            className="h-[38px] w-full rounded-lg border border-border bg-background px-3 text-sm text-text-primary"
          >
            <option value="">Unassigned</option>
            {deptOptions.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <FieldLabel hint="Shown on the org-chart">Reports to</FieldLabel>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => set({ reportsTo: null })}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-all ${
                form.reportsTo == null
                  ? "border-accent bg-accent/[0.08] text-text-primary"
                  : "border-border bg-surface text-text-primary hover:bg-surface-hover"
              }`}
            >
              <span>None (top-level)</span>
            </button>
            {agents.map((a) => {
              const selected = form.reportsTo === a.id;
              return (
                <button
                  key={a.id}
                  onClick={() => set({ reportsTo: a.id })}
                  className={`inline-flex items-center gap-2 rounded-full border py-1.5 pl-2 pr-3 text-[12.5px] font-medium transition-all ${
                    selected
                      ? "border-accent bg-accent/[0.08] text-text-primary"
                      : "border-border bg-surface text-text-primary hover:bg-surface-hover"
                  }`}
                >
                  <MiniAvatar size={20} />
                  <span>{a.name}</span>
                  {agentRoleLabel(a) && (
                    <span className="text-[11.5px] text-text-muted">· {agentRoleLabel(a)}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="md:col-span-2">
          <FieldLabel hint="One or two sentences. Fed into the agent's identity and instructions.">
            Core Responsibilities
          </FieldLabel>
          <textarea
            value={form.capabilities ?? ""}
            onChange={(e) => set({ capabilities: e.target.value })}
            placeholder="What is this agent responsible for? What shouldn't it do?"
            className="min-h-[92px] w-full resize-y rounded-lg border border-border bg-background px-3 py-2.5 text-[13.5px] leading-relaxed text-text-primary outline-none focus:border-accent"
          />
        </div>
      </div>
    </div>
  );
}

// ═════════════════ STEP — WORKSPACE ═════════════════
// Also reused by the subagent wizard (NewSubagent.tsx) with its own header copy.
export function StepWorkspace({
  form,
  set,
  companyId,
  eyebrow = "Step 3 of 6 · Workspace",
  title = "Review the agent's workspace files",
  desc = "Generated now so the agent lands ready to work — no waiting after deploy. Edit anything, or regenerate.",
}: {
  form: CreateAgentInput;
  set: Updater;
  companyId: string;
  eyebrow?: string;
  title?: string;
  desc?: string;
}) {
  const ws = form.workspace ?? {};
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const result = await agentsApi.workspacePreview(companyId, {
        name: form.name,
        role: form.role,
        title: form.title,
        department: form.department,
        capabilities: form.capabilities,
        reportsTo: form.reportsTo,
      });
      set({ workspace: result });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to generate workspace");
    } finally {
      setGenerating(false);
    }
  };

  // Auto-generate a draft on first entry if empty, so it's ready while the user
  // moves through the remaining steps — the agent then lands without waiting on
  // the LLM after deploy.
  useEffect(() => {
    const empty = !ws.identityMd && !ws.soulMd && !ws.userMd && !ws.agentsMd;
    if (empty && !generating) void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields: Array<{ key: "identityMd" | "soulMd" | "userMd" | "agentsMd"; label: string; hint: string }> = [
    { key: "identityMd", label: "IDENTITY.md", hint: "Who the agent is + its responsibilities" },
    { key: "soulMd", label: "SOUL.md", hint: "Personality, values, how it behaves" },
    { key: "userMd", label: "USER.md", hint: "The organisation & people it serves" },
    { key: "agentsMd", label: "AGENTS.md", hint: "Peer agents it can coordinate with" },
  ];

  return (
    <div>
      <SectionHeader eyebrow={eyebrow} title={title} desc={desc} />
      <div className="mb-5 flex items-center gap-3">
        <Button variant="secondary" onClick={generate} disabled={generating}>
          {generating ? "Generating…" : ws.identityMd || ws.soulMd ? "Regenerate" : "Generate"}
        </Button>
        {generating && <span className="text-xs text-text-muted">Drafting identity files…</span>}
        {error && <span className="text-sm text-danger">{error}</span>}
      </div>
      <div className="flex flex-col gap-4">
        {fields.map((f) => (
          <div key={f.key}>
            <FieldLabel hint={f.hint}>{f.label}</FieldLabel>
            <textarea
              value={(ws[f.key] as string | undefined) ?? ""}
              onChange={(e) => set({ workspace: { ...ws, [f.key]: e.target.value } })}
              placeholder={generating ? "Generating…" : ""}
              className="min-h-[150px] w-full resize-y rounded-lg border border-border bg-background px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-text-primary outline-none focus:border-accent"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ═════════════════ STEP 3 — BRAIN ═════════════════
export function StepBrain({
  form,
  set,
  catalogueModels,
}: {
  form: CreateAgentInput;
  set: Updater;
  catalogueModels: ModelCatalogueItem[];
}) {
  const selectedModel =
    (form.adapterConfig?.model as string | undefined) ?? "claude-sonnet-4-5";

  // Pick a "recommended" model — prefer the default, else first sonnet, else first.
  const recommendedId =
    catalogueModels.find((m) => m.isDefault)?.modelId ??
    catalogueModels.find((m) => m.modelId.includes("sonnet"))?.modelId ??
    catalogueModels[0]?.modelId;

  const primaryAdapters = ADAPTERS.filter((a) => !a.advanced);
  const advancedAdapters = ADAPTERS.filter((a) => a.advanced);

  const formatCost = (m: ModelCatalogueItem): string => {
    const fmt = (c: number | null) =>
      c == null ? "?" : c >= 100 ? `$${(c / 100).toFixed(0)}` : `$${(c / 100).toFixed(2)}`;
    return `${fmt(m.costPer1mInput)} / ${fmt(m.costPer1mOutput)} per 1M tokens`;
  };

  return (
    <div>
      <SectionHeader
        eyebrow="Step 3 of 5 · Brain"
        title="Pick the model and agent runtime"
        desc="The runtime decides how the agent thinks and what tools it can reach. The model is its raw intelligence."
      />

      <FieldLabel>Runtime</FieldLabel>
      <div className="mb-6 grid grid-cols-1 gap-2.5 md:grid-cols-3">
        {primaryAdapters.map((a) => {
          const Icon = a.icon;
          const selected =
            form.adapterType === (a.adapterType ?? a.id) &&
            ((form.adapterConfig?.runtimeMode as string | undefined) ?? undefined) === a.runtimeMode;
          return (
            <SelectableCard
              key={a.id}
              selected={selected}
              onClick={() => {
                const nextConfig = { ...(form.adapterConfig ?? {}) } as Record<string, unknown>;
                if (a.runtimeMode) nextConfig.runtimeMode = a.runtimeMode;
                else delete nextConfig.runtimeMode;
                set({ adapterType: a.adapterType ?? a.id, adapterConfig: nextConfig });
              }}
              className="!p-3.5"
            >
              <div className="mb-1.5 flex items-center gap-2.5">
                <Icon
                  size={18}
                  className={selected ? "text-accent" : "text-text-secondary"}
                />
                <span className="text-[13.5px] font-semibold text-text-primary">
                  {a.label}
                </span>
              </div>
              <p className="m-0 text-xs leading-snug text-text-muted">{a.desc}</p>
            </SelectableCard>
          );
        })}
      </div>

      <details className="mb-6">
        <summary className="cursor-pointer select-none text-[12.5px] text-text-muted">
          Advanced runtimes (subprocess, HTTP webhook)
        </summary>
        <div className="mt-2.5 grid grid-cols-1 gap-2.5 md:grid-cols-2">
          {advancedAdapters.map((a) => {
            const Icon = a.icon;
            const selected =
              form.adapterType === (a.adapterType ?? a.id) &&
              ((form.adapterConfig?.runtimeMode as string | undefined) ?? undefined) === a.runtimeMode;
            return (
              <SelectableCard
                key={a.id}
                selected={selected}
                onClick={() => {
                  const nextConfig = { ...(form.adapterConfig ?? {}) } as Record<string, unknown>;
                  if (a.runtimeMode) nextConfig.runtimeMode = a.runtimeMode;
                  else delete nextConfig.runtimeMode;
                  set({ adapterType: a.adapterType ?? a.id, adapterConfig: nextConfig });
                }}
                className="!p-3.5"
              >
                <div className="mb-1 flex items-center gap-2.5">
                  <Icon size={16} className="text-text-secondary" />
                  <span className="text-[13.5px] font-semibold">{a.label}</span>
                </div>
                <p className="m-0 text-xs text-text-muted">{a.desc}</p>
              </SelectableCard>
            );
          })}
        </div>
      </details>

      <FieldLabel hint="Usage counts against your monthly agent budget">Model</FieldLabel>
      <div className="flex flex-col gap-2">
        {catalogueModels.length === 0 && (
          <p className="text-sm text-text-muted">No models available in the catalogue yet.</p>
        )}
        {catalogueModels.map((m) => {
          const sel = selectedModel === m.modelId;
          return (
            <button
              key={m.id}
              onClick={() =>
                set({
                  adapterConfig: { ...(form.adapterConfig ?? {}), model: m.modelId },
                })
              }
              className={`flex items-center gap-3.5 rounded-[10px] border p-3 text-left transition-all ${
                sel
                  ? "border-accent bg-accent/[0.04] shadow-[0_0_0_3px_rgba(16,163,127,0.10)]"
                  : "border-border bg-surface hover:bg-surface-hover"
              }`}
            >
              <div
                className={`h-[18px] w-[18px] shrink-0 rounded-full ${
                  sel
                    ? "border-[5px] border-accent bg-accent shadow-[inset_0_0_0_3px_var(--background)]"
                    : "border border-border bg-background"
                }`}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-[13.5px] font-semibold">{m.displayName}</span>
                  <span className="text-[11px] text-text-muted">· {m.provider}</span>
                  {m.modelId === recommendedId && <Pill tone="accent">Recommended</Pill>}
                </div>
              </div>
              <span className="font-mono text-xs text-text-muted">{formatCost(m)}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6">
        <FieldLabel hint="Used automatically if the primary model is unavailable or errors">
          Backup model (optional)
        </FieldLabel>
        <select
          value={(form.adapterConfig?.fallbackModel as string | undefined) ?? ""}
          onChange={(e) =>
            set({
              adapterConfig: {
                ...(form.adapterConfig ?? {}),
                fallbackModel: e.target.value || undefined,
              },
            })
          }
          className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-[13.5px] text-text-primary outline-none focus:border-accent"
        >
          <option value="">Platform default</option>
          {catalogueModels
            .filter((m) => m.modelId !== selectedModel)
            .map((m) => (
              <option key={m.id} value={m.modelId}>
                {m.displayName} · {m.provider}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
}

// ═════════════════ STEP 4 — INFRA ═════════════════
export function StepInfra({
  form,
  set,
}: {
  form: CreateAgentInput;
  set: Updater;
}) {
  const vmId = (form.runtimeConfig?.vm_size as string | undefined) ?? "Standard_B2s";
  const regionId = (form.runtimeConfig?.region as string | undefined) ?? "uksouth";
  const vm = VM_SIZES.find((v) => v.id === vmId) ?? VM_SIZES[1];

  const setRuntime = (patch: Record<string, unknown>) =>
    set({ runtimeConfig: { ...(form.runtimeConfig ?? {}), ...patch } });

  return (
    <div>
      <SectionHeader
        eyebrow="Step 4 of 5 · Infrastructure"
        title="Where should we host the agent?"
        desc="Each agent runs on its own dedicated Azure VM. Pick a size and a region close to your users."
      />

      <FieldLabel hint="You can resize later without downtime">VM size</FieldLabel>
      <div className="mb-6 grid grid-cols-2 gap-2.5 md:grid-cols-3">
        {VM_SIZES.map((v) => {
          const sel = vmId === v.id;
          return (
            <SelectableCard
              key={v.id}
              selected={sel}
              onClick={() => setRuntime({ vm_size: v.id })}
              className="!p-3.5"
            >
              <div className="flex items-center justify-between">
                <span className="text-[13.5px] font-semibold">{v.label}</span>
                {v.recommended && <Pill tone="accent">Rec</Pill>}
              </div>
              <p className="m-0 mt-1 mb-2 text-xs text-text-muted">{v.spec}</p>
              <div className="flex items-baseline justify-between">
                <span className="text-base font-semibold text-text-primary">
                  ${v.cost}
                  <span className="text-[11px] font-normal text-text-muted"> /mo</span>
                </span>
                <span className="text-[10.5px] uppercase tracking-[0.05em] text-text-muted">
                  {v.tier}
                </span>
              </div>
            </SelectableCard>
          );
        })}
      </div>

      <FieldLabel>Region</FieldLabel>
      <div className="mb-6 flex flex-wrap gap-1.5">
        {REGIONS.map((r) => {
          const sel = regionId === r.id;
          return (
            <button
              key={r.id}
              onClick={() => setRuntime({ region: r.id })}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-[12.5px] font-medium transition-all ${
                sel
                  ? "border-accent bg-accent/[0.08] text-text-primary"
                  : "border-border bg-surface text-text-primary hover:bg-surface-hover"
              }`}
            >
              <MapPin
                size={13}
                className={sel ? "text-accent" : "text-text-muted"}
              />
              <span>{r.label}</span>
              <span className="text-[11.5px] text-text-muted">· {r.city}</span>
            </button>
          );
        })}
      </div>

      {/* Cost preview */}
      <div className="flex items-center gap-4 rounded-[10px] border border-border bg-surface px-4 py-3.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
          <DollarSign size={18} />
        </div>
        <div className="flex-1">
          <p className="m-0 text-xs font-semibold uppercase tracking-[0.05em] text-text-muted">
            Estimated monthly cost
          </p>
          <p className="m-0 mt-0.5 text-sm text-text-primary">
            <span className="text-[22px] font-semibold">${vm.cost}</span>
            <span className="ml-1.5 text-text-muted">VM</span>
            <span className="mx-2 text-text-muted">+</span>
            <span>model usage</span>
            <span className="ml-2 text-text-muted">(metered, capped at agent budget)</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// ═════════════════ STEP 5 — REVIEW ═════════════════
export function StepReview({
  form,
  agents,
  catalogueModels,
}: {
  form: CreateAgentInput;
  agents: Agent[];
  catalogueModels: ModelCatalogueItem[];
}) {
  const vmId = (form.runtimeConfig?.vm_size as string | undefined) ?? "Standard_B2s";
  const regionId = (form.runtimeConfig?.region as string | undefined) ?? "uksouth";
  const modelId = (form.adapterConfig?.model as string | undefined) ?? "claude-sonnet-4-5";

  const vm = VM_SIZES.find((v) => v.id === vmId);
  const region = REGIONS.find((r) => r.id === regionId);
  const adapter = findAdapterCard(form.adapterType, form.adapterConfig?.runtimeMode);
  const model = catalogueModels.find((m) => m.modelId === modelId);
  const boss = agents.find((a) => a.id === form.reportsTo);

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex border-b border-border py-3 last:border-b-0">
      <span className="flex-[0_0_180px] text-xs font-semibold uppercase tracking-[0.05em] text-text-muted">
        {label}
      </span>
      <div className="flex-1 text-[13.5px] text-text-primary">{children}</div>
    </div>
  );

  const AdapterIcon = adapter?.icon;

  return (
    <div>
      <SectionHeader
        eyebrow="Step 5 of 5 · Review"
        title="Ready to deploy?"
        desc="Deployment takes about 2 minutes. You can watch it live on the next screen, or close this tab and come back."
      />

      <div className="rounded-xl border border-border bg-surface px-5 py-1">
        <Row label="Identity">
          <div className="flex items-center gap-2.5">
            <MiniAvatar size={28} />
            <div>
              <div className="font-semibold">{form.name || "Unnamed agent"}</div>
              {form.title && (
                <div className="text-xs text-text-muted">
                  {form.title}
                  {form.department ? ` · ${form.department}` : ""}
                </div>
              )}
            </div>
          </div>
        </Row>
        <Row label="Reports to">
          {boss ? (
            <>
              {boss.name}
              {boss.role && <span className="text-text-muted"> · {boss.role}</span>}
            </>
          ) : (
            "None"
          )}
        </Row>
        <Row label="Runtime">
          <div className="flex items-center gap-2">
            {AdapterIcon && <AdapterIcon size={14} className="text-accent" />}
            <span>{adapter?.label}</span>
            <span className="text-text-muted">·</span>
            <span>{model?.displayName ?? modelId}</span>
          </div>
        </Row>
        <Row label="Hosting">
          <span className="font-mono text-[12.5px]">{vm?.label}</span>
          <span className="mx-1.5 text-text-muted">·</span>
          <span>{vm?.spec}</span>
          <span className="mx-1.5 text-text-muted">·</span>
          <span>{region?.label}</span>
        </Row>
        <Row label="Core Responsibilities">
          {form.capabilities ? (
            <p className="m-0 leading-relaxed text-text-secondary">{form.capabilities}</p>
          ) : (
            <span className="text-text-muted">Not specified</span>
          )}
        </Row>
        <Row label="Estimated cost">
          <span className="text-base font-semibold">${vm?.cost}/mo</span>
          <span className="ml-2 text-text-muted">VM, plus model usage</span>
        </Row>
      </div>

      <p className="mt-3.5 flex items-center gap-1.5 text-xs text-text-muted">
        <Shield size={13} /> Deployed into your own Azure subscription. You own the VM and the
        data.
      </p>
    </div>
  );
}
